import { CellState, cellKey as key, queenAttacks } from './board';

// The Queens elimination rules, as a raw enumerator.
//
// This module answers "what deductions are available on this board?" and nothing else. It is
// deliberately free of presentation (titles, explanations), ranking, deduplication, capping,
// and — critically — of any call to solve(). The offline puzzle analyzer calls it in a tight
// fixpoint loop, so it must stay cheap and total.
//
// src/client/hints.ts is the player-facing view over this: it adds the R0 mistake pass and the
// R1 queen-reach hints, turns instances into prose, then ranks and caps them for the panel.
// See HINTS_DESIGN.md for what each rule means and why.
//
// Nothing here may import from src/client/**.

export type RuleId = 'R2' | 'R3' | 'R4' | 'R5' | 'R6' | 'R7' | 'R8' | 'R9';

/** A row, column, or color region a deduction reasons about. */
export type HintUnit =
  | { type: 'row'; index: number }
  | { type: 'col'; index: number }
  | { type: 'region'; color: string };

/**
 * Fine-grained technique identity — the key for difficulty profiling, per-player proficiency
 * fitting, and "generate a puzzle that trains X". Two instances of the same rule can be very
 * different techniques to a human (a two-cell region shadow vs a sprawling one), so the rule
 * id alone is too coarse.
 */
export interface TechniqueSig {
  rule: RuleId;
  /** Shape/orientation discriminator — see sigOf* below. */
  variant: string;
  /** Cardinality of the reasoning set: pigeonhole size, or candidate count. */
  n: number;
}

/** Stable string form, with n bucketed so the signature space stays small enough to fit. */
export function sigKey(s: TechniqueSig): string {
  const n = s.n <= 1 ? '1' : s.n === 2 ? '2' : s.n === 3 ? '3' : '4+';
  return `${s.rule}/${s.variant}/${n}`;
}

export interface RuleInstance {
  sig: TechniqueSig;
  kind: 'eliminate' | 'place';
  /** Rows / columns / regions this deduction reasons about. */
  units: HintUnit[];
  /** The live candidate cells that drive the deduction. */
  causeCells: string[];
  /** Eliminated cells, or the single forced cell for a placement. */
  cells: string[];
  /** Ranking weight: eliminations removed, or squares a forced placement would clear. */
  impact: number;
}

export interface EnumerateOptions {
  /** Rules to skip. Used to compute which techniques a puzzle strictly requires, by ablation. */
  disable?: ReadonlySet<RuleId>;
  /** Skip the expensive searches (R5/R6 subset walk, R9 pair walk). */
  cheapOnly?: boolean;
}

const rowUnit = (index: number): HintUnit => ({ type: 'row', index });
const colUnit = (index: number): HintUnit => ({ type: 'col', index });
const regionUnit = (color: string): HintUnit => ({ type: 'region', color });

export interface Analysis {
  size: number;
  colorRegions: string[][];
  candKeys: Set<string>;
  /** Queens currently on the board. */
  queens: Array<[number, number]>;
  /** Distinct color labels that still need a queen. */
  openColors: string[];
  /** Rows / columns that still need a queen. */
  openRows: number[];
  openCols: number[];
  candByColor: Map<string, Array<[number, number]>>;
  candByRow: Map<number, Array<[number, number]>>;
  candByCol: Map<number, Array<[number, number]>>;
  attackCache: Map<string, Set<string>>;
  spatialCache: Map<string, Set<string>>;
}

export function analyze(board: CellState[][], colorRegions: string[][], visibleXs?: Set<string>): Analysis {
  const size = board.length;

  const queens: Array<[number, number]> = [];
  const queenColors = new Set<string>();
  const queenRows = new Set<number>();
  const queenCols = new Set<number>();
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (board[r][c] !== 'queen') continue;
      queens.push([r, c]);
      queenColors.add(colorRegions[r][c]);
      queenRows.add(r);
      queenCols.add(c);
    }
  }

  const candKeys = new Set<string>();
  const candByColor = new Map<string, Array<[number, number]>>();
  const candByRow = new Map<number, Array<[number, number]>>();
  const candByCol = new Map<number, Array<[number, number]>>();
  const allColors = new Set<string>();

  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      allColors.add(colorRegions[r][c]);
      // Only visible marks remove a square: an empty cell is a candidate even if a placed
      // queen attacks it. Hints must never rely on eliminations the player can't see, so a
      // square only stops counting once it shows an X (or a queen). visibleXs carries the
      // auto-X cells when that setting is on — those are displayed, so they count too.
      if (board[r][c] !== null || visibleXs?.has(key(r, c))) continue;
      const color = colorRegions[r][c];
      candKeys.add(key(r, c));
      (candByColor.get(color) ?? candByColor.set(color, []).get(color)!).push([r, c]);
      (candByRow.get(r) ?? candByRow.set(r, []).get(r)!).push([r, c]);
      (candByCol.get(c) ?? candByCol.set(c, []).get(c)!).push([r, c]);
    }
  }

  const openColors = [...allColors].filter(col => !queenColors.has(col));
  const openRows: number[] = [];
  const openCols: number[] = [];
  for (let i = 0; i < size; i++) {
    if (!queenRows.has(i)) openRows.push(i);
    if (!queenCols.has(i)) openCols.push(i);
  }

  return { size, colorRegions, candKeys, queens, openColors, openRows, openCols, candByColor, candByRow, candByCol, attackCache: new Map(), spatialCache: new Map() };
}

export function attacksOf(A: Analysis, r: number, c: number): Set<string> {
  const k = key(r, c);
  let set = A.attackCache.get(k);
  if (!set) {
    set = new Set(queenAttacks(r, c, A.colorRegions));
    A.attackCache.set(k, set);
  }
  return set;
}

// The *spatial* squares a queen on (r,c) rules out: its row, its column, and its 8 neighbours —
// but NOT the same-color region cells that queenAttacks() also folds in. R2 phrases its deduction
// spatially ("wherever this line's queen goes it attacks these squares"), so it must reason with
// this set; letting the same-color cells in makes it silently absorb R4's color deduction (a line
// that is a slice of one region), producing a correct elimination with a wrong explanation.
export function spatialAttacksOf(A: Analysis, r: number, c: number): Set<string> {
  const k = key(r, c);
  let set = A.spatialCache.get(k);
  if (!set) {
    set = new Set<string>();
    for (let i = 0; i < A.size; i++) {
      if (i !== c) set.add(key(r, i));
      if (i !== r) set.add(key(i, c));
    }
    for (let dr = -1; dr <= 1; dr++)
      for (let dc = -1; dc <= 1; dc++) {
        if (dr === 0 && dc === 0) continue;
        const nr = r + dr, nc = c + dc;
        if (nr >= 0 && nr < A.size && nc >= 0 && nc < A.size) set.add(key(nr, nc));
      }
    A.spatialCache.set(k, set);
  }
  return set;
}

// Cells attacked by *every* possible placement of a unit's queen, restricted to current
// candidates. Whichever candidate the unit's queen ends up on, it attacks all of these, so
// none of them can hold a queen. This single mechanism powers R2/R3/R7. `atkOf` selects the attack
// model — the full one (default) for the region/line-of-a-color rules, or the spatial-only one for
// R2 (see spatialAttacksOf).
function unitShadow(A: Analysis, cands: Array<[number, number]>, atkOf = attacksOf): string[] {
  let inter: Set<string> | null = null;
  for (const [r, c] of cands) {
    const atk = atkOf(A, r, c);
    if (inter === null) {
      inter = new Set(atk);
    } else {
      for (const k of [...inter]) if (!atk.has(k)) inter.delete(k);
    }
  }
  if (!inter) return [];
  return [...inter].filter(k => A.candKeys.has(k));
}

function sameLine(cands: Array<[number, number]>): 'row' | 'col' | null {
  if (cands.length < 2) return null;
  if (cands.every(([r]) => r === cands[0][0])) return 'row';
  if (cands.every(([, c]) => c === cands[0][1])) return 'col';
  return null;
}

/** Squares a queen on (r,c) would newly rule out — used to weight forced placements. */
function placementImpact(A: Analysis, r: number, c: number): number {
  let n = 0;
  for (const k of attacksOf(A, r, c)) if (A.candKeys.has(k)) n++;
  return n;
}

/**
 * Geometry class of a region's candidate set — the R7 variant. A two-cell region whose squares
 * touch is a completely different thing to spot than a six-cell sprawl, and the profiler needs
 * to tell them apart.
 */
function regionShape(cands: Array<[number, number]>): string {
  if (cands.length < 2) return 'single';
  const rs = cands.map(([r]) => r), cs = cands.map(([, c]) => c);
  const h = Math.max(...rs) - Math.min(...rs) + 1;
  const w = Math.max(...cs) - Math.min(...cs) + 1;
  if (cands.length === 2) {
    const [[r1, c1], [r2, c2]] = cands;
    return Math.max(Math.abs(r1 - r2), Math.abs(c1 - c2)) === 1 ? 'adj2' : 'pair';
  }
  if (h === 1 || w === 1) return 'line';
  if (h <= 2 && w <= 2) return cands.length === 3 ? 'Lshape' : 'block';
  return 'spread';
}

/**
 * Every deduction available on this board, un-ranked, un-deduped and uncapped, in a fixed
 * emission order (R8, R3/R7, R2, R4, R5/R6, R9). The order is load-bearing: computeHints sorts
 * this list with a stable sort, so ties resolve by emission order and the panel's output
 * depends on it.
 *
 * visibleXs: cells currently *displayed* as Xs without being board marks (the auto-X cells when
 * the Auto Xs setting is on). The engine reasons only from what the player can see.
 */
export function enumerateInstances(
  board: CellState[][],
  colorRegions: string[][],
  visibleXs?: Set<string>,
  opts?: EnumerateOptions,
): RuleInstance[] {
  const A = analyze(board, colorRegions, visibleXs);
  return enumerateFrom(A, opts);
}

/** Same as enumerateInstances but reusing an existing Analysis (the caller already built one). */
export function enumerateFrom(A: Analysis, opts?: EnumerateOptions): RuleInstance[] {
  const out: RuleInstance[] = [];
  const on = (r: RuleId) => !opts?.disable?.has(r);
  const cheapOnly = opts?.cheapOnly ?? false;

  // R8 — forced placements (a unit with a single candidate). Deduped by target cell.
  if (on('R8')) {
    const placed = new Set<string>();
    const addPlace = (r: number, c: number, variant: string, unit: HintUnit) => {
      const k = key(r, c);
      if (placed.has(k)) return;
      placed.add(k);
      out.push({
        sig: { rule: 'R8', variant, n: 1 },
        kind: 'place', units: [unit], causeCells: [], cells: [k],
        impact: placementImpact(A, r, c),
      });
    };
    for (const color of A.openColors) {
      const cs = A.candByColor.get(color);
      if (cs && cs.length === 1) addPlace(cs[0][0], cs[0][1], 'region', regionUnit(color));
    }
    for (const r of A.openRows) {
      const cs = A.candByRow.get(r);
      if (cs && cs.length === 1) addPlace(cs[0][0], cs[0][1], 'row', rowUnit(r));
    }
    for (const c of A.openCols) {
      const cs = A.candByCol.get(c);
      if (cs && cs.length === 1) addPlace(cs[0][0], cs[0][1], 'col', colUnit(c));
    }
  }

  // R3 / R7 — region shadow. Cells attacked by every placement of a color's queen are out. When
  // the color's options all sit in one row/column, the eliminations *inside that line* are the
  // friendly R3 "locked to a line" case; any further shadow cells outside the line (reached by
  // adjacency, not by the line itself) are a plain region shadow (R7). Split so that neither
  // explanation claims squares it doesn't cover.
  for (const color of A.openColors) {
    const cs = A.candByColor.get(color)!;
    if (cs.length < 2) continue;
    const elim = unitShadow(A, cs);
    if (elim.length === 0) continue;
    const cause = cs.map(([r, c]) => key(r, c));
    const line = sameLine(cs);
    const addRegionShadow = (cells: string[]) => {
      if (cells.length === 0 || !on('R7')) return;
      out.push({
        sig: { rule: 'R7', variant: regionShape(cs), n: cs.length },
        kind: 'eliminate', units: [regionUnit(color)], causeCells: cause, cells,
        impact: cells.length,
      });
    };
    if (line) {
      const lineIdx = line === 'row' ? cs[0][0] : cs[0][1];
      const onLine = (k: string) => {
        const [r, c] = k.split(',').map(Number);
        return line === 'row' ? r === lineIdx : c === lineIdx;
      };
      const lineElim = elim.filter(onLine);
      const lineUnit = line === 'row' ? rowUnit(cs[0][0]) : colUnit(cs[0][1]);
      if (lineElim.length && on('R3')) {
        out.push({
          sig: { rule: 'R3', variant: line, n: cs.length },
          kind: 'eliminate', units: [regionUnit(color), lineUnit], causeCells: cause,
          cells: lineElim, impact: lineElim.length,
        });
      }
      addRegionShadow(elim.filter(k => !onLine(k)));
    } else {
      addRegionShadow(elim);
    }
  }

  // R2 — row/column squeeze. Cells attacked by every placement of a line's queen are out.
  if (on('R2')) {
    const addLineShadow = (cands: Array<[number, number]> | undefined, variant: string, unit: HintUnit) => {
      if (!cands || cands.length < 2) return;
      const elim = unitShadow(A, cands, spatialAttacksOf); // spatial only — color deductions are R4's
      if (elim.length === 0) return;
      out.push({
        sig: { rule: 'R2', variant, n: cands.length },
        kind: 'eliminate', units: [unit], causeCells: cands.map(([r, c]) => key(r, c)),
        cells: elim, impact: elim.length,
      });
    };
    for (const r of A.openRows) addLineShadow(A.candByRow.get(r), 'row', rowUnit(r));
    for (const c of A.openCols) addLineShadow(A.candByCol.get(c), 'col', colUnit(c));
  }

  // R4 — a line whose only candidates are one color forces that color into the line, so that
  // color is ruled out everywhere else.
  if (on('R4')) {
    const addLineOwned = (cands: Array<[number, number]> | undefined, variant: string, unit: HintUnit) => {
      if (!cands || cands.length < 2) return;
      const color = A.colorRegions[cands[0][0]][cands[0][1]];
      if (!cands.every(([r, c]) => A.colorRegions[r][c] === color)) return;
      const inLine = new Set(cands.map(([r, c]) => key(r, c)));
      const elim = A.candByColor.get(color)!.map(([r, c]) => key(r, c)).filter(k => !inLine.has(k));
      if (elim.length === 0) return;
      out.push({
        sig: { rule: 'R4', variant, n: cands.length },
        kind: 'eliminate', units: [unit, regionUnit(color)], causeCells: cands.map(([r, c]) => key(r, c)),
        cells: elim, impact: elim.length,
      });
    };
    for (const r of A.openRows) addLineOwned(A.candByRow.get(r), 'row', rowUnit(r));
    for (const c of A.openCols) addLineOwned(A.candByCol.get(c), 'col', colUnit(c));
  }

  if (!cheapOnly) {
    pigeonhole(A, out, on);
    if (on('R9')) pairShadow(A, out);
  }

  return out;
}

// A tight set of `n` members occupying exactly `n` slots (colors→lines for R5, lines→colors for R6)
// is only worth reporting when it's *minimal* — no proper, non-empty subset of its members is itself
// tight. A non-minimal set is really two or more independent pigeonholes glued together (e.g. three
// colors sharing three columns, plus an unrelated color alone in a fourth column). Reporting the
// union conflates distinct deductions and produces a confusing "eliminates 10" spanning unrelated
// regions; we skip it and let the smaller minimal sets — and R3/R4 for the size-1 pieces — fire on
// their own. `memberSlots[i]` is the set of slots member `i` (a bit in `mask`) touches. Exported
// for direct testing.
export function hasProperTightSubset<T>(mask: number, memberSlots: Set<T>[]): boolean {
  for (let sub = (mask - 1) & mask; sub > 0; sub = (sub - 1) & mask) {
    const union = new Set<T>();
    let cnt = 0;
    for (let i = 0; i < memberSlots.length; i++) {
      if (!(sub & (1 << i))) continue;
      cnt++;
      for (const x of memberSlots[i]) union.add(x);
    }
    if (union.size <= cnt) return true; // a proper subset already fills its own slots
  }
  return false;
}

// R5: N colors whose candidates fit within N rows (or columns) own those lines — every other
// color is ruled out there. R6: the dual — N lines spanning only N colors confine those colors.
function pigeonhole(A: Analysis, out: RuleInstance[], on: (r: RuleId) => boolean): void {
  const colors = A.openColors;

  // R5 over rows then columns.
  if (on('R5')) for (const axis of ['row', 'col'] as const) {
    const lineOf = (r: number, c: number) => (axis === 'row' ? r : c);
    const m = colors.length;
    // Lines each color touches, indexed by the same bit positions used in `mask` — drives both the
    // tightness test and the minimality check.
    const memberLines = colors.map(col => {
      const s = new Set<number>();
      for (const [r, c] of A.candByColor.get(col)!) s.add(lineOf(r, c));
      return s;
    });
    for (let mask = 1; mask < (1 << m); mask++) {
      const n = popcount(mask);
      if (n < 2 || n >= m) continue;
      const lines = new Set<number>();
      const cause: string[] = [];
      let bail = false;
      for (let i = 0; i < m && !bail; i++) {
        if (!(mask & (1 << i))) continue;
        for (const [r, c] of A.candByColor.get(colors[i])!) {
          lines.add(lineOf(r, c));
          cause.push(key(r, c));
          if (lines.size > n) { bail = true; break; }
        }
      }
      if (bail || lines.size !== n) continue;
      if (hasProperTightSubset(mask, memberLines)) continue; // decomposes into smaller pigeonholes
      const inSet = (i: number) => (mask & (1 << i)) !== 0;
      const colorIdx = new Map(colors.map((col, i) => [col, i]));
      const elim: string[] = [];
      for (const line of lines) {
        const cands = axis === 'row' ? A.candByRow.get(line) : A.candByCol.get(line);
        for (const [r, c] of cands ?? []) {
          const idx = colorIdx.get(A.colorRegions[r][c]);
          if (idx === undefined || !inSet(idx)) elim.push(key(r, c));
        }
      }
      if (elim.length === 0) continue;
      const cells = dedupeKeys(elim);
      out.push({
        sig: { rule: 'R5', variant: axis === 'row' ? 'rows' : 'cols', n },
        kind: 'eliminate',
        units: [...lines].map(l => (axis === 'row' ? rowUnit(l) : colUnit(l))),
        causeCells: cause, cells, impact: cells.length,
      });
    }
  }

  // R6 over rows then columns.
  if (on('R6')) for (const axis of ['row', 'col'] as const) {
    const openLines = axis === 'row' ? A.openRows : A.openCols;
    const candOf = (line: number) => (axis === 'row' ? A.candByRow.get(line) : A.candByCol.get(line)) ?? [];
    const m = openLines.length;
    // Colors each line contains, indexed by the same bit positions used in `mask`.
    const memberColors = openLines.map(line => {
      const s = new Set<string>();
      for (const [r, c] of candOf(line)) s.add(A.colorRegions[r][c]);
      return s;
    });
    for (let mask = 1; mask < (1 << m); mask++) {
      const n = popcount(mask);
      if (n < 2 || n >= m) continue;
      const setColors = new Set<string>();
      const cause: string[] = [];
      let bail = false;
      const chosen: number[] = [];
      for (let i = 0; i < m && !bail; i++) {
        if (!(mask & (1 << i))) continue;
        chosen.push(openLines[i]);
        for (const [r, c] of candOf(openLines[i])) {
          setColors.add(A.colorRegions[r][c]);
          cause.push(key(r, c));
          if (setColors.size > n) { bail = true; break; }
        }
      }
      if (bail || setColors.size !== n) continue;
      if (hasProperTightSubset(mask, memberColors)) continue; // decomposes into smaller pigeonholes
      const chosenSet = new Set(chosen);
      const elim: string[] = [];
      for (const color of setColors) {
        for (const [r, c] of A.candByColor.get(color) ?? []) {
          if (!chosenSet.has(axis === 'row' ? r : c)) elim.push(key(r, c));
        }
      }
      if (elim.length === 0) continue;
      const cells = dedupeKeys(elim);
      out.push({
        sig: { rule: 'R6', variant: axis === 'row' ? 'rows' : 'cols', n },
        kind: 'eliminate',
        units: chosen.map(l => (axis === 'row' ? rowUnit(l) : colUnit(l))),
        causeCells: cause, cells, impact: cells.length,
      });
    }
  }
}

// R9 — pairwise joint shadow. Two units (color/row/column) that each still need a queen hold two
// distinct, mutually non-attacking queens in the solution (or a single shared queen where they
// overlap). For every such legal placement of the pair, union the squares the two would attack;
// intersect that over all legal pairs. Any candidate attacked by *every* legal pair can't hold a
// queen. This generalizes the single-unit shadow (R2/R3/R7) — the mutual-non-attack constraint
// prunes pairs, so it catches eliminations that only surface when two regions/lines interact.
// Sound: the real solution's two queens are one of the enumerated legal pairs, so any square in
// the intersection is attacked by the true placement too. (Reasoning over the visible-candidate
// superset only shrinks the intersection, never enlarges it — same conservative guarantee as R7.)
export type UnitKind = 'color' | 'row' | 'col';
function pairShadow(A: Analysis, out: RuleInstance[]): void {
  const units: Array<{ cands: Array<[number, number]>; kind: UnitKind; hintUnit: HintUnit }> = [];
  for (const color of A.openColors) { const cs = A.candByColor.get(color); if (cs && cs.length) units.push({ cands: cs, kind: 'color', hintUnit: regionUnit(color) }); }
  for (const r of A.openRows) { const cs = A.candByRow.get(r); if (cs && cs.length) units.push({ cands: cs, kind: 'row', hintUnit: rowUnit(r) }); }
  for (const c of A.openCols) { const cs = A.candByCol.get(c); if (cs && cs.length) units.push({ cands: cs, kind: 'col', hintUnit: colUnit(c) }); }

  for (let i = 0; i < units.length; i++) {
    for (let j = i + 1; j < units.length; j++) {
      const C1 = units[i].cands, C2 = units[j].cands;
      let inter: Set<string> | null = null;
      let anyPair = false;
      let overlap = false;
      for (const [r1, c1] of C1) {
        const a1 = attacksOf(A, r1, c1);
        for (const [r2, c2] of C2) {
          const same = r1 === r2 && c1 === c2;
          if (same) overlap = true;
          if (!same && a1.has(key(r2, c2))) continue; // the pair would attack each other — impossible together
          anyPair = true;
          const u = new Set(a1);
          if (!same) for (const k of attacksOf(A, r2, c2)) u.add(k);
          if (inter === null) inter = u;
          else { for (const k of [...inter]) if (!u.has(k)) inter.delete(k); }
          if (inter.size === 0) break;
        }
        if (inter && inter.size === 0) break;
      }
      if (!anyPair || !inter || inter.size === 0) continue;
      const elim = [...inter].filter(k => A.candKeys.has(k));
      if (elim.length === 0) continue;
      out.push({
        // Emission order is colors, then rows, then cols, so the kind pair is already canonical.
        sig: {
          rule: 'R9',
          variant: `${units[i].kind}x${units[j].kind}${overlap ? '+ovl' : ''}`,
          n: Math.max(C1.length, C2.length),
        },
        kind: 'eliminate',
        units: [units[i].hintUnit, units[j].hintUnit],
        causeCells: [...C1, ...C2].map(([r, c]) => key(r, c)),
        cells: elim, impact: elim.length,
      });
    }
  }
}

function popcount(x: number): number {
  let n = 0;
  while (x) { x &= x - 1; n++; }
  return n;
}

function dedupeKeys(keys: string[]): string[] {
  return [...new Set(keys)];
}
